import { Payload } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { EventEmitter, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { SessionStatus } from 'src/app/models/member';
import { AlertService } from '../alert.service';
import { Utilities } from '../utilities';
import { FeatureService } from './../../shared/preview-feature/preview-feature.service';
import { CommandLazyDataService } from './command/command-lazy-data.service';
import {
    CommandLifeCycleEvent,
    ICommand,
    ICommandCancelConfig,
    ICommandContext,
    ICommandLifeCycle,
    ICommandService,
    ICommandsWrapper
} from './command/command.models';
import { CommandService } from './command/command.service';
import { SystemEvaluatorService } from './system-evaluator/system-evaluator.service';
import { TaskHostDefault } from './task-host-default';
import {
    ITaskCommandWrapper,
    ITaskHost,
    ITaskWrapper,
    TaskExecutionTime,
    TaskInfoExecutionData,
    TaskInfoRunnableEvaluation,
    TaskNotification
} from './task.state.models';


export declare type TaskServiceFactoryFn = () => TaskService;
export const TaskServiceFactory = new InjectionToken<TaskServiceFactoryFn>('TaskService');

export const taskServiceFactory = (systemEvaluatorService: SystemEvaluatorService,
    commandService: CommandService,
    featureService: FeatureService,
    alertService: AlertService,
    router: Router,
    commandLazyDataService: CommandLazyDataService) => {
    return () => {
        return new TaskService(systemEvaluatorService, commandService, featureService, alertService, router, commandLazyDataService);
    }
}

export class TaskService extends NgxsDataRepository<ICommandsWrapper> implements ICommandService {

    private _taskHost: ITaskHost;
    get host() {
        return this._taskHost;
    }
    notifications$ = new Subject<TaskNotification[]>();
    onCompleteRunnigCommands: () => Promise<any>;

    commandEvent$ = new EventEmitter<ICommandLifeCycle>();
    taskProcessDate: Date;

    get commandsSync() {
        return this._taskHost?.trackingCommandsSync.map(_ => _ as ITaskCommandWrapper);
    }

    get commands$() {
        return this._taskHost.trackingCommands$.pipe(map(commands => commands.map(_ => _ as ITaskCommandWrapper)));
    }

    get taskCommands$() {
        return this.commands$.pipe(map(commands => commands && commands.filter(cmd => cmd.task)));
    }

    get taskCommandsSync() {
        return this.commandsSync?.filter(cmd => cmd.task);
    }

    get completedTaskCommandsSync() {
        return this.taskCommandsSync?.filter(_ => _.task.executionData.status.value === CommandLifeCycleEvent.Completed);
    }

    get completedTaskCommands$() {
        return this.taskCommands$.pipe(map(commands => commands.filter(_ => _.task.executionData.status.value === CommandLifeCycleEvent.Completed)));
    }

    get pendingTaskCommandsSync() {
        return this.taskCommandsSync?.filter(cmd => [CommandLifeCycleEvent.Completed, CommandLifeCycleEvent.Disabled].every(statusItem => cmd.task.executionData.status.value !== statusItem));
    }

    get pendingTaskCommands$() {
        return this.taskCommands$.pipe(map(commands => commands.filter(cmd => [CommandLifeCycleEvent.Completed, CommandLifeCycleEvent.Disabled].some(statusItem => cmd.task.executionData.status.value !== statusItem))));
    }

    get notCompletedTaskCommandsSync() {
        return this.taskCommandsSync?.filter(cmd => cmd.task.executionData.status.value !== CommandLifeCycleEvent.Completed);
    }

    get executingTaskCommandSync() {
        return this.taskCommandsSync?.find(cmd => cmd.task.executionData.status.value === CommandLifeCycleEvent.Executing);
    }

    get executableTaskCommandsSync(): ITaskCommandWrapper[] {
        return this.pendingTaskCommandsSync?.filter(cmd => cmd.task.executionData.evaluation.value === true);
    }

    get executableStartTaskCommandsSync(): ITaskCommandWrapper[] {
        return this.executableTaskCommandsSync?.filter(cmd => cmd.task.executionTime === TaskExecutionTime.Start);
    }

    get executableEndTaskCommandsSync(): ITaskCommandWrapper[] {
        return this.executableTaskCommandsSync?.filter(cmd => cmd.task.executionTime === TaskExecutionTime.End);
    }

    constructor(
        protected systemEvaluatorService: SystemEvaluatorService,
        protected commandService: CommandService,
        protected featureService: FeatureService,
        protected alertService: AlertService,
        protected router: Router,
        private commandLazyDataService: CommandLazyDataService
    ) {
        super();
    }

    orderTasksToDisplay(taskedCmds: ITaskCommandWrapper[]): ITaskCommandWrapper[] {
        const result = [...taskedCmds];
        result.sort((a, b) => {
            return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
        });

        return result;
    }

    orderTasksToRun(taskedCmds: ITaskCommandWrapper[]): ITaskCommandWrapper[] {
        const result = [...taskedCmds];
        result.sort((a, b) => {
            if ([SessionStatus.Open, null].some(stationStatus => stationStatus == this.systemEvaluatorService.member.sessionStatus)) {
                if (a.task.executionTime === TaskExecutionTime.Start && b.task.executionTime === TaskExecutionTime.Start) {
                    return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
                } else if (a.task.executionTime === TaskExecutionTime.Start) {
                    return -1
                } else if (b.task.executionTime === TaskExecutionTime.Start) {
                    return 1
                } else {
                    return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
                }
                // else{
                //     return (a.task.executionTime === TaskExecutionTime.Start) ? -1 : 1;
                // }
            } else if ([SessionStatus.Closing, SessionStatus.Closed].find(status => status === this.systemEvaluatorService.member.sessionStatus)) {
                if (a.task.executionTime === TaskExecutionTime.End && b.task.executionTime === TaskExecutionTime.End) {
                    return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
                } else if (a.task.executionTime === TaskExecutionTime.End) {
                    return -1
                } else if (b.task.executionTime === TaskExecutionTime.End) {
                    return 1
                } else {
                    return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
                }
            } else {
                return (a.task.order || 0) > (b.task.order || 0) ? 1 : -1;
            }
        });

        return result;
    }

    //#region Core Functions

    setContext(context?: ITaskHost) {
        this._taskHost = context || new TaskHostDefault();
        return this;
    }

    async activate() {
        await this.featureService.initialized$.pipe(filter(_ => !!_), first()).toPromise();
        await this.cleanExecution();
        this._taskHost.commandHostReady$.pipe(filter(ready => ready), switchMap(_ => {
            return this.commandEvent$.pipe(filter(event => (event.command as ITaskCommandWrapper).task && event.event === CommandLifeCycleEvent.Completed));
        }))
            .subscribe(async _ => {
                this._taskHost.tryNextCommand();
            });

        this._taskHost.commandHostReady$.pipe(filter(ready => ready), switchMap(_ => {
            console.log('TASKS: Member initialized processing tasks => ', _);

            return this._taskHost.commands$.pipe(filter(commands => commands && commands.length > 0), tap(commands => {
                console.log('TASKS: Member commands => ', commands);
                console.log('TASKS: Member in task service => ', (this._taskHost as any).memberSync);
                this.taskProcessDate = new Date();
            }), map(commands => {
                return commands as ITaskCommandWrapper[];
            }));
        })).subscribe(async (commands) => {
            const newCommands = this.mergeCommands(commands);
            await this.updateCommands(newCommands);

            await this.runCommands();
        });
    }

    async runCommands(refreshCommands = false) {
        if (refreshCommands) {
            const commands = this.mergeCommands(this.commandsSync);
            await this.updateCommands(commands);
        }

        if (!this.executingTaskCommandSync && this.executableTaskCommandsSync.length > 0) {
            const executables = this.orderTasksToRun(this.executableTaskCommandsSync);
            const taskCmd = executables[0];
            if (
                /*Match null*/(taskCmd.task.executionTime == null) ||
                /*Match Start*/(taskCmd.task.executionTime === TaskExecutionTime.Start && [SessionStatus.Open, null].some(_ => _ == this.systemEvaluatorService.member.sessionStatus)) ||
                /*Match End*/(taskCmd.task.executionTime === TaskExecutionTime.End && [SessionStatus.Closing, SessionStatus.Closed].find(status => status === this.systemEvaluatorService.member.sessionStatus))
            ) {
                if (!taskCmd.task.executionData.executionOrder) {
                    taskCmd.task.executionData.executionOrder = Math.max(...this.taskCommandsSync.map(_ => _.task.executionData.executionOrder || 0));
                    taskCmd.task.executionData.executionOrder += 1;
                }
                setTimeout(async () => {
                    await this.setCommandStatus(taskCmd, CommandLifeCycleEvent.Executing);
                    await this.runTask(taskCmd);
                }, 0);

            }
        }
    }

    async runTask(taskCmd: ITaskCommandWrapper) {
        await this.commandService.runCommand(taskCmd, { host: this._taskHost, commandService: this, runAsTask: true } as ICommandContext);
    }




    /**
     * Merge commands based on task status
     * C: Command  |  T: Task  |  TP: Task and Pending   |  TC: Task and Completed  |  TD: Task and Disabled
     *
     *
     *  UI                DB              New UI
     *  _       ---->      _      ---->     _
     *  _       ---->      C      ---->     C
     *  _       ---->      C T    ---->     C TN
     *  C       ---->      _      ---->     _
     *  C       ---->      C      ---->     C
     *  C       ---->      C T    ---->     C TN
     *  C TP    ---->      C      ---->     C TP -> TD
     *  C TP    ---->      C T    ---->     C TP
     *  C TC    ---->      C      ---->     C TC
     *  C TC    ---->      C T    ---->     C TC
     *  C TD    ---->      C      ---->     C TD
     *  C TD    ---->      C T    ---->     C TD -> TN
     */

    private mergeCommands(newCommands: ITaskCommandWrapper[]) {
        const result = [...Utilities.cloneHard(newCommands)];
        for (const newCommand of result) {
            const currentCommand = this.commandsSync.find(currentItem => currentItem.id === newCommand.id) as ITaskWrapper;
            // only enter if one of the commands has task
            if (newCommand.task) {
                this.newTaskCommandMerge(currentCommand, newCommand);
            } else if (currentCommand && currentCommand.task) {
                newCommand.task = currentCommand.task;
                if (![CommandLifeCycleEvent.Completed, CommandLifeCycleEvent.Disabled].some(status => currentCommand.task.executionData.status.value === status)) {
                    newCommand.task.executionData.previousStatus = newCommand.task.executionData.status;
                    newCommand.task.executionData.status = this.createNewExecutionData().status;
                    newCommand.task.executionData.status.value = CommandLifeCycleEvent.Disabled;
                }
            }
        }

        this.evaluate(result);

        this.refreshNotifications(result);

        return result;
    }

    private newTaskCommandMerge(currentCommand: ITaskWrapper, newCommand: ITaskCommandWrapper) {
        if (currentCommand && currentCommand.task) {
            newCommand.task.executionData = currentCommand.task.executionData;
            if (currentCommand.task.executionData.status.value === CommandLifeCycleEvent.Disabled) {
                newCommand.task.executionData.previousStatus = newCommand.task.executionData.status;
                newCommand.task.executionData.status = this.createNewExecutionData().status;
            }
        } else {
            newCommand.task.executionData = this.createNewExecutionData();
        }
    }

    private createNewExecutionData(): TaskInfoExecutionData {
        return {
            status: {
                value: CommandLifeCycleEvent.New,
                date: this.taskProcessDate
            },
            evaluation: null,
            previousEvaluation: null,

        } as TaskInfoExecutionData;
    }


    private evaluate(commands: ITaskCommandWrapper[]) {
        for (const command of commands) {
            if (command.task) {
                const executionData = (command.task.executionData || this.createNewExecutionData());
                if (!this.markedAsCompleted(command)) {
                    command.task.executionData = executionData;
                    command.task.executionData.previousEvaluation = command.task.executionData.evaluation;
                    command.task.executionData.evaluation = {
                        successful: command.task.successfulCondition != null ? this.systemEvaluatorService.evaluate(command.task.successfulCondition) : null,
                        value: this.systemEvaluatorService.evaluate(command.task.condition),
                        date: new Date()
                    } as TaskInfoRunnableEvaluation;
                }

                const taskEvaluation = executionData.evaluation || {} as TaskInfoRunnableEvaluation;
                taskEvaluation.successful = command.task.successfulCondition != null ? this.systemEvaluatorService.evaluate(command.task.successfulCondition) : null;
            }

        }

    }

    private refreshNotifications(commands?: ITaskCommandWrapper[]) {
        commands = commands || this.commandsSync;
        const newTasks = commands.filter(taskCmd => {
            return taskCmd.task && taskCmd.task.notification && taskCmd.task.executionData.status.value !== CommandLifeCycleEvent.Completed && !taskCmd.task.executionData.dismissedNotification && taskCmd.task.executionData.evaluation.value === true;
        });

        const notifications = newTasks.map(item => ({
            level: item.task.notification.level,
            header: item.task.displayName,
            icon: item.task.icon,
            details: item.task.notification.details,
            dismissible: item.task.notification.dismissible,
            sourceCommand: item
        } as TaskNotification));

        this.notifications$.next(notifications);
    }

    async markCommandAsCanceled(cmd?: ITaskCommandWrapper, cancelConfig?: ICommandCancelConfig) {
        cmd = cmd || this.executingTaskCommandSync;
        await this.cancelCommand(cmd);
        if (cancelConfig?.stopChain) {
            const result = [...Utilities.cloneHard(this.commandsSync)];
            this.evaluate(result);
            this.updateCommands(result);
        } else {
            await this.afterMarkedAsCanceled(cmd, cancelConfig);
        }
    }

    async afterMarkedAsCanceled(cmd: ITaskCommandWrapper, cancelConfig?: ICommandCancelConfig): Promise<void> {
        return Promise.resolve(null);
    }

    async markCommandAsExecuting(cmd: ITaskCommandWrapper, commandParse: any) {
        this.commandLazyDataService.executingCommandParse = commandParse;
        await this.setCommandStatus(cmd, CommandLifeCycleEvent.Executing);
        this.commandEvent$.next({ event: CommandLifeCycleEvent.Executing, command: cmd } as ICommandLifeCycle);
    }

    async markCommandAsCompleted(cmd: ITaskCommandWrapper) {
        this.commandLazyDataService.executingCommandParse = undefined;
        await this.setCommandStatus(cmd, CommandLifeCycleEvent.Completed);
        setTimeout(() => {
            this.commandEvent$.next({ event: CommandLifeCycleEvent.Completed, command: cmd } as ICommandLifeCycle);
        }, 0);
    }



    protected async cancelCommand(cmd: ITaskCommandWrapper) {
        this.commandLazyDataService.executingCommandParse = undefined;
        await this.setCommandStatus(cmd, CommandLifeCycleEvent.Canceled);

        // Reset the end tasks always
        const toReset = this.taskCommandsSync.filter(commandItem => commandItem.task.executionTime === TaskExecutionTime.End && commandItem.task.allowReset);
        for (const toResetItem of toReset) {
            await this.setCommandStatus(toResetItem, null);
        }
    }

    protected async setCommandStatus(cmd: ITaskCommandWrapper, status: CommandLifeCycleEvent) {
        cmd = Utilities.cloneHard(cmd);
        if (!cmd.task.executionData) {
            cmd.task.executionData = this.createNewExecutionData();
        }
        cmd.task.executionData.previousStatus = cmd.task.executionData.status;
        cmd.task.executionData.status = {
            value: status,
            date: new Date()
        };
        await this.upsertCommand(cmd);
    }

    async upsertCommand(@Payload('action') cmd: ITaskCommandWrapper) {
        await this._taskHost.updateTrackingCommand(cmd);
    }

    setCommandExecutionMetadata(command: ICommand, data: any) {
        const taskWrapper = (command as ITaskCommandWrapper);
        taskWrapper.task.executionData.metadata = { ...taskWrapper.task.executionData.metadata, ...data };
        this._taskHost.updateTrackingCommand(command);
    }

    private async updateCommands(@Payload('commands') commands: ITaskCommandWrapper[]) {
        await this._taskHost.setTrackingCommands(commands);
    }

    private async cleanExecution() {
        if (this.executingTaskCommandSync) {
            await this.setCommandStatus(this.executingTaskCommandSync, this.executingTaskCommandSync.task.executionData.previousStatus.value);
        }
    }

    //#endregion

    //#region Utils
    markedAsCompleted(taskName: string | (ICommand & ITaskWrapper)) {
        const command: ICommand & ITaskWrapper = this.getTask(taskName);

        if (command && command.task) {
            return (command.task.executionData.status.value === CommandLifeCycleEvent.Completed);
        } else {
            console.log(`Task ${taskName} is not registered yet`);
        }
    }

    markedAsExecuting(taskName: string | (ICommand & ITaskWrapper)) {
        const command: ICommand & ITaskWrapper = this.getTask(taskName);

        if (command && command.task) {
            return (command.task.executionData.status.value === 'Executing');
        } else {
            console.log(`Task ${taskName} is not registered yet`);
        }
    }

    private getTask(taskName: string | (ICommand & ITaskWrapper)) {
        let command: ICommand & ITaskWrapper;
        if (typeof taskName === 'string') {
            const formattedTaskName = taskName.toUpperCase();
            command = this.commandsSync.map(_ => _ as ICommand & ITaskWrapper).find(action => action.task && action.task.name.toUpperCase() === formattedTaskName);
        } else {
            command = taskName;
        }
        return command;
    }

    dismissNotification(command: ITaskCommandWrapper) {
        const newCommand = Utilities.cloneHard(command);
        newCommand.task.executionData.dismissedNotification = true;
        this._taskHost.updateTrackingCommand(newCommand);
        this.refreshNotifications();
    }
    //#endregion

}

@Injectable({
    providedIn: 'root'
})
export class TaskLifeCycleService {
    finishTask = new EventEmitter<any>();
}
